<script setup lang="ts">

import { computed, defineProps, type PropType, ref } from 'vue'
import type OreConversion from '@/types/OreConversion.ts'
import { formatShort } from '@/models/formatNumber.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  conversion: {
    type: Object as PropType<OreConversion>,
    required: true
  },
  objectImage: {
    type: String,
    required: true
  },
  width: {
    type: Number,
    required: true
  },
  height: {
    type: Number,
    required: true
  },
  top: {
    type: Number,
    required: true
  },
  left: {
    type: Number,
    required: true
  },
  centerX: {
    type: Number,
    required: false,
    default: null
  },
  firstTime: {
    type: Number,
    required: false,
    default: 0.25
  },
  secondTime: {
    type: Number,
    required: false,
    default: 0.5
  },
  thirdTime: {
    type: Number,
    required: false,
    default: 0.75
  },
  minSpeed: {
    type: Number,
    required: false,
    default: 1
  },
  debug: {
    type: Boolean,
    required: false,
    default: false
  }
})

const hover = ref(false);

const conveyorColor = computed(() => {
  if (props.conversion.capacity == 0) {
    return '#151E31';
  }
  return 'black';
});

const oreSpeed = computed(() => {
  if (props.conversion.capacity <= 0) {
    return 0;
  }

  const logValue = Math.log10(1000 / props.conversion.capacity);
  return Math.max(props.minSpeed, logValue);
});

const centerX = computed(() => {
  return props.centerX !== null ? props.centerX : (props.width / 2) - 0.5;
});

const centerY = computed(() => {
  return (props.height / 2) - 0.5;
});

</script>

<template>
  <svg :class="{
          'absolute':true,
          'border':debug
        }"
       :style="'top:' + top + 'rem;left:' + left + 'rem;width:' + width + 'rem;height:' + height + 'rem;'"
       xmlns="http://www.w3.org/2000/svg"
  >

    <!-- Background rails - TR to TC -->
    <rect :x="centerX + 'rem'"
          y="0"
          :width="(width - centerX) + 'rem'"
          height="1rem"
          :fill="conveyorColor"
          @mouseenter="hover = true"
          @mouseout="hover = false"/>

    <!-- Background rails - TC to BC -->
    <rect :x="centerX + 'rem'"
          y="0"
          width="1rem"
          :height="(centerY + 1) + 'rem'"
          :fill="conveyorColor"
          @mouseenter="hover = true"
          @mouseout="hover = false"/>

    <!-- Background rails - BC to BL -->
    <rect x="0"
          :y="centerY + 'rem'"
          :width="(centerX + 1) + 'rem'"
          height="1rem"
          :fill="conveyorColor"
          @mouseenter="hover = true"
          @mouseout="hover = false"/>

    <image
      v-if="conversion.capacity > 0"
      :href="props.objectImage"
      :x="(width - 1) + 'rem'"
      y="0"
      width="1rem"
      height="1rem"
      @mouseenter="hover = true"
      @mouseout="hover = false"
      class="cursor-pointer"
    >
      <!-- Animate the x movement: TR->TC->BC->BL -->
      <animate
        attributeName="x"
        :values="(width - 1) + 'rem;' + centerX + 'rem;' + centerX + 'rem;0'"
        :keyTimes="'0;' + firstTime + ';' + secondTime + ';1'"
        :dur="oreSpeed + 's'"
        repeatCount="indefinite" />
      <!-- Animate the y movement: TR->TC->BC->BL -->
      <animate
        attributeName="y"
        :values="'0;0;' + centerY + 'rem;' + centerY + 'rem'"
        :keyTimes="'0;' + firstTime + ';' + secondTime + ';1'"
        :dur="oreSpeed + 's'"
        repeatCount="indefinite" />
    </image>

  </svg>

  <div v-if="props.conversion.capacity>0 && hover" class="absolute text-green-500 border p-1 rounded border-slate-600 bg-slate-800 text-center" :style="'top:' + (top + 0.5) + 'rem;left:' + (left - 1) + 'rem; width:5rem;z-index:18;'">{{ formatShort(props.conversion.capacity) }}<small class="text-subtext"> {{ t('resources.conveyor.perWeek') }}</small></div>
</template>

<style scoped lang="scss">

</style> 